// Login.jsx
import { useState, useContext, useEffect } from "react"; // 👈 ADDED useEffect
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Avatar,
  Stack,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress 
} from "@mui/material";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { loginAdmin } from "../api/adminApi";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { registerNotificationToken } from "../notifications/notification";
import { setupForegroundNotification } from "../notifications/foregroundNotification";
import { fetchIP } from "../utils/fetchIP";

// Modern input styling matching the application's aesthetic
const modernInputStyle = {
  '& .MuiInputLabel-root': {
    color: '#A59D96',
    fontWeight: '600',
    bgcolor: '#FFFFFF',
    px: 0.5,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#D97706'
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#ECE5DA',
    },
    '&:hover fieldset': {
      borderColor: '#F29D17',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F8C332',
      borderWidth: '2px'
    }
  },
  '& .MuiOutlinedInput-input': {
    fontWeight: '700',
    color: '#2F2A25',
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #FFFFFF inset !important',
      WebkitTextFillColor: '#2F2A25 !important',
      caretColor: '#2F2A25',
      borderRadius: 'inherit',
    }
  }
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); 

  // 👇 UPDATED: Extracting admin and token alongside login
  const { admin, token, login } = useContext(AuthContext);
  const navigate = useNavigate();

  // 👇 NEW: Auto-redirect if already logged in when opening the PWA/Web
  useEffect(() => {
    const savedToken = localStorage.getItem("admin_access_token");
    const savedAdmin = localStorage.getItem("admin");

    if ((token || savedToken) && (admin || savedAdmin)) {
      navigate("/dashboard", { replace: true });
    }
  }, [admin, token, navigate]);

  // State for the modern alert
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error"
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async () => {
    setIsLoading(true); 

    try {
      // PROTECT: Wrap IP fetch so blockers don't crash the login
      let ip = "UNKNOWN";
      try {
        ip = await fetchIP();
      } catch (ipError) {
        console.warn("Could not fetch IP, continuing...", ipError);
      }

      const res = await loginAdmin({ email, password, frontend_ip: ip });

      // PROTECT: Explicitly check for backend errors returned as 200 OK
      if (res.data.error) {
        setSnackbar({ open: true, message: res.data.error, severity: "error" });
        return;
      }

      login(res.data);
      // 👇 UPDATED: Renamed to prevent variable shadowing with context 'admin'
      const loggedInAdmin = res.data.admin;

      if (loggedInAdmin.role === "ZONAL_ADMIN") {
        setupForegroundNotification();
        await registerNotificationToken(loggedInAdmin.id, "ZONAL_ADMIN");
      }

      navigate("/dashboard", { replace: true });

    } catch (err) {
      console.log(err);
      const errorMessage = err?.response?.data?.error || "Login failed. Check your network.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setIsLoading(false); 
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #FFFDF8 0%, #FFF5D1 100%)",
        px: 2,
        boxSizing: "border-box"
      }}
    >
      {/* Decorative Background Blobs */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          left: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          bgcolor: 'rgba(248, 195, 50, 0.15)',
          filter: 'blur(60px)',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          right: -100,
          width: 450,
          height: 450,
          borderRadius: '50%',
          bgcolor: 'rgba(232, 93, 63, 0.1)',
          filter: 'blur(80px)',
          zIndex: 0
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: 400,
          borderRadius: "24px",
          bgcolor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow: "0 10px 32px rgba(58, 44, 35, 0.08), 0 4px 8px rgba(58, 44, 35, 0.04)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 1,
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 16px 40px rgba(58, 44, 35, 0.12), 0 8px 16px rgba(58, 44, 35, 0.06)",
          }
        }}
      >
        <Avatar
          sx={{
            bgcolor: '#FFF6DE',
            color: '#F29D17',
            width: 64,
            height: 64,
            mb: 2
          }}
        >
          <AdminPanelSettingsIcon fontSize="large" />
        </Avatar>

        <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", mb: 0.5 }}>
          Mealno Admin
        </Typography>
        <Typography variant="body2" fontWeight="600" sx={{ color: "#A59D96", mb: 3 }}>
          Sign in to manage your portal
        </Typography>

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
          sx={{ width: "100%" }}
        >
          <Stack spacing={2.5} sx={{ width: "100%", mb: 3 }}>
            <TextField
              label="Email Address"
              fullWidth
              sx={modernInputStyle}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading} 
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              sx={modernInputStyle}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading} 
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      sx={{ color: '#A59D96' }}
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Button
            type="submit"
            variant="contained"
            disableElevation
            fullWidth
            disabled={isLoading}
            sx={{
              py: 1.5,
              borderRadius: '16px',
              bgcolor: isLoading ? '#E0DCD6' : '#F8C332',
              color: isLoading ? '#A59D96' : '#3A2C23',
              fontWeight: '900',
              textTransform: 'none',
              fontSize: '1.05rem',
              boxShadow: isLoading ? 'none' : '0 4px 12px rgba(248, 195, 50, 0.3)',
              '&:hover': { bgcolor: isLoading ? '#E0DCD6' : '#F5B71D' },
              '&.Mui-disabled': {
                bgcolor: '#E0DCD6', 
                color: '#8A8580'
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={26} color="inherit" />
            ) : (
              "Secure Login"
            )}
          </Button>
        </Box>
      </Paper>

      {/* Modern Snackbar for Alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}